import { useState, useEffect, useCallback } from "react";
import { useToast } from "../../../basecomp/components/provider/ToastProvider";
import {
    fetchAllDatabases,
    fetchDatabaseTables,
    fetchTableData,
    insertTableRow,
    updateTableRow,
    deleteTableRow,
    addTableColumn,
    modifyTableColumn,
    deleteTableColumn,
    truncateTable,
    dropTable,
    exportTableData,
    importTableData,
    createTable,
    executeCustomQueryWithFilter
} from "../model/DataModel";

const DataManagementVM = () => {
    const { handleToastOpen } = useToast();

    const [openTabs, setOpenTabs] = useState([]);
    const [databases, setDatabases] = useState([]);
    const [selectedDatabase, setSelectedDatabase] = useState('');
    const [tables, setTables] = useState([]);
    const [selectedTable, setSelectedTable] = useState('');
    const [tableData, setTableData] = useState([]);
    const [isDatabaseLoading, setIsDatabaseLoading] = useState(false);
    const [isTableLoading, setIsTableLoading] = useState(false);
    const [tableColumns, setTableColumns] = useState([]);
    const [dataTypes, setDataTypes] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [originalTableData, setOriginalTableData] = useState([]);
    const [filteredTableData, setFilteredTableData] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterColumn, setFilterColumn] = useState('');
    const [filterValue, setFilterValue] = useState('');
    const [searchType, setSearchType] = useState('');
    const [sortColumn, setSortColumn] = useState('');
    const [sortDirection, setSortDirection] = useState('ASC');
    const [editingRow, setEditingRow] = useState(null);
    const [editingData, setEditingData] = useState({});
    const [selectedRows, setSelectedRows] = useState([]);
    const [visibleColumns, setVisibleColumns] = useState([]);
    const [showColumnDrawer, setShowColumnDrawer] = useState(false);
    const [showQueryDialog, setShowQueryDialog] = useState(false);
    const [customQuery, setCustomQuery] = useState('');
    const [showColumnDialog, setShowColumnDialog] = useState(false);
    const [columnDialogMode, setColumnDialogMode] = useState('add');
    const [columnDialogData, setColumnDialogData] = useState({});
    const [showImportDialog, setShowImportDialog] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importOptions, setImportOptions] = useState({
        continueOnError: true,
        ignoreExtraColumns: false
    });
    const [activeTab, setActiveTab] = useState(0);
    const [isAddingNewRow, setIsAddingNewRow] = useState(false);
    const [actionMenu, setActionMenu] = useState({ anchor: null, rowId: null });
    const [headerMenu, setHeaderMenu] = useState({ anchor: null, column: null });
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
    const [deleteType, setDeleteType] = useState('selected');
    const [showNewTableDialog, setShowNewTableDialog] = useState(false);
    const [sqlTemplateTab, setSqlTemplateTab] = useState(0);
    const [isExpanded, setIsExpanded] = useState(true);
    const [hasUserDelete, setHasUserDelete] = useState(false);
    const [hasUserDrop, setHasUserDrop] = useState(false);
    const [hasUserTruncate, setHasUserTruncate] = useState(false);
    const [showExportDialog, setShowExportDialog] = useState(false);
    const [exportFormat, setExportFormat] = useState('csv');
    const [activeQuery, setActiveQuery] = useState('');
    const [showActiveQuery, setShowActiveQuery] = useState(false);
    const [newTableData, setNewTableData] = useState({
        tableName: '',
        columns: [
            {
                id: 1,
                name: '',
                dataType: '',
                isPrimaryKey: false,
                allowNull: true,
                defaultValue: ''
            }
        ]
    });
    const [createTableMenu, setCreateTableMenu] = useState(false);
    const [isFromSqlCreation, setIsFromSqlCreation] = useState(false);

    useEffect(() => {
        if (selectedDatabase) {
            loadTables(selectedDatabase);
            loadDataTypes(selectedDatabase);
        } else {
            setTables([]);
            setSelectedTable('');
            resetTableData();
        }
    }, [selectedDatabase]);

    useEffect(() => {
        if (selectedDatabase && selectedTable) {
            loadTableData();
        }
    }, [selectedDatabase, selectedTable, rowsPerPage, sortColumn, sortDirection]);

    useEffect(() => {
        applyFiltersAndSort();
    }, [originalTableData, searchQuery, filterColumn, filterValue, searchType, sortColumn, sortDirection]);

    const handleUnifiedDelete = async () => {
        try {
            setIsLoading(true);

            if (deleteType === 'selected') {
                if (selectedRows.length === 0) {
                    handleToastOpen('error', 'No rows selected for deletion');
                    return;
                }

                const response = await deleteTableRow(selectedDatabase, selectedTable, selectedRows);

                if (response.status === 200) {
                    handleToastOpen(`'success', Successfully deleted ${selectedRows.length} row(s)`);
                    setSelectedRows([]);
                    await loadTableData();
                } else {
                    handleToastOpen('error', 'Failed to delete rows');
                }

            } else if (deleteType === 'truncate') {
                const response = await truncateTable(selectedDatabase, selectedTable);

                if (response.status === 200) {
                    handleToastOpen(`'success', Table "${selectedTable}" truncated successfully`);
                    await loadTableData();
                } else {
                    handleToastOpen('error', 'Failed to truncate table');
                }

            } else if (deleteType === 'drop') {
                const response = await dropTable(selectedDatabase, selectedTable);

                if (response.status === 200) {
                    handleToastOpen('success', `Table "${selectedTable}" dropped successfully`);

                    setSelectedTable('');
                    setTableData([]);
                    setOriginalTableData([]);
                    setFilteredTableData([]);
                    setTableColumns([]);
                    setVisibleColumns([]);
                    setSelectedRows([]);
                    setTotalRows(0);
                    setPage(0);
                    setEditingRow(null);
                    setEditingData({});
                    setIsAddingNewRow(false);
                    setSearchQuery('');
                    setFilterColumn('');
                    setFilterValue('');
                    setSearchType('');
                    setSortColumn('');
                    setSortDirection('ASC');

                    await loadTables(selectedDatabase);
                } else {
                    handleToastOpen('error', 'Failed to drop table');
                }
            }

        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || 'Unknown error';

            if (deleteType === 'selected') {
                handleToastOpen('error', `Failed to delete rows: ${errorMessage}`);
            } else if (deleteType === 'truncate') {
                handleToastOpen('error', `Failed to truncate table: ${errorMessage}`);
            } else if (deleteType === 'drop') {
                handleToastOpen('error', `Failed to drop table: ${errorMessage}`);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const applyFiltersAndSort = useCallback(() => {
        let filtered = [...originalTableData];

        if (searchQuery && searchQuery.trim()) {
            const searchTerm = searchQuery.toLowerCase().trim();
            filtered = filtered.filter(row => {
                return visibleColumns.some(columnName => {
                    const cellValue = row[columnName];
                    if (cellValue == null) return false;
                    return String(cellValue).toLowerCase().includes(searchTerm);
                });
            });
        }

        if (filterColumn && filterValue && searchType) {
            const filterTerm = filterValue.toLowerCase().trim();
            filtered = filtered.filter(row => {
                const cellValue = row[filterColumn];
                if (cellValue == null) return false;

                const stringValue = String(cellValue).toLowerCase();

                if (searchType === 'contains') {
                    return stringValue.includes(filterTerm);
                } else if (searchType === 'exact') {
                    return stringValue === filterTerm;
                } else if (searchType === 'starts_with') {
                    return stringValue.startsWith(filterTerm);
                } else if (searchType === 'ends_with') {
                    return stringValue.endsWith(filterTerm);
                } else if (searchType === 'not_contains') {
                    return !stringValue.includes(filterTerm);
                } else if (searchType === 'empty') {
                    return !cellValue || cellValue === '';
                } else if (searchType === 'not_empty') {
                    return cellValue && cellValue !== '';
                }

                return true;
            });
        }

        if (sortColumn) {
            filtered.sort((a, b) => {
                const aVal = a[sortColumn];
                const bVal = b[sortColumn];

                if (aVal == null && bVal == null) return 0;
                if (aVal == null) return 1;
                if (bVal == null) return -1;

                let comparison = 0;
                if (typeof aVal === 'string' && typeof bVal === 'string') {
                    comparison = aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: 'base' });
                } else if (typeof aVal === 'number' && typeof bVal === 'number') {
                    comparison = aVal - bVal;
                } else if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
                    comparison = aVal === bVal ? 0 : aVal ? 1 : -1;
                } else {
                    comparison = String(aVal).localeCompare(String(bVal), undefined, {
                        numeric: true,
                        sensitivity: 'base'
                    });
                }

                return sortDirection === 'DESC' ? -comparison : comparison;
            });
        }

        setFilteredTableData(filtered);
        setTotalRows(filtered.length);

        const maxPage = Math.max(0, Math.ceil(filtered.length / rowsPerPage) - 1);
        if (page > maxPage) {
            setPage(0);
        }
    }, [originalTableData, searchQuery, filterColumn, filterValue, searchType, sortColumn, sortDirection, visibleColumns, page, rowsPerPage]);

    const handleSearchChange = useCallback((query) => {
        setSearchQuery(query);
        setPage(0);
    }, []);

    const handleFilterChange = useCallback((column, value) => {
        setFilterColumn(column);
        setFilterValue(value);
        setPage(0);
    }, []);

    const handleSearchTypeChange = useCallback((type) => {
        setSearchType(type);
        setPage(0);
    }, []);

    const clearAllFilters = useCallback(() => {
        setSearchQuery('');
        setFilterColumn('');
        setFilterValue('');
        setSearchType('');
        setPage(0);
    }, []);

    const getCurrentPageData = useCallback(() => {
        const startIndex = page * rowsPerPage;
        const endIndex = startIndex + rowsPerPage;
        return filteredTableData.slice(startIndex, endIndex);
    }, [filteredTableData, page, rowsPerPage]);

    useEffect(() => {
        setTableData(getCurrentPageData());
    }, [getCurrentPageData]);

    const generateColumnsFromData = (data) => {
        if (!data || data.length === 0) return [];

        const sampleRow = data[0];
        return Object.keys(sampleRow).map(key => ({
            id: key,
            name: key,
            dataType: detectColumnType(sampleRow[key]),
            formattedType: detectColumnType(sampleRow[key]),
            nullable: true,
            primaryKey: key === 'id' || key.toLowerCase().includes('id'),
            position: Object.keys(sampleRow).indexOf(key) + 1
        }));
    };

    const detectColumnType = (value) => {
        if (value === null || value === undefined) return 'TEXT';
        if (typeof value === 'number') {
            return Number.isInteger(value) ? 'INTEGER' : 'NUMERIC';
        }
        if (typeof value === 'boolean') return 'BOOLEAN';
        if (typeof value === 'string') {
            if (/^\d{4}-\d{2}-\d{2}/.test(value)) return 'TIMESTAMP';
            if (/@/.test(value)) return 'CHARACTER VARYING';
            return 'CHARACTER VARYING';
        }
        return 'TEXT';
    };

    const loadDataTypes = async () => {
        setDataTypes([
            // Integer types
            'INT', 'BIGINT', 'SMALLINT',
            
            // Numeric types
            'NUMERIC', 'DECIMAL', 'REAL', 'DOUBLE PRECISION', 'MONEY',
            
            // Character types
            'VARCHAR(255)', 'VARCHAR(100)', 'VARCHAR(50)', 'CHARACTER', 'CHAR(10)', 'TEXT',
            
            // Boolean
            'BOOLEAN',
            
            // Date/Time types
            'DATE', 'TIMESTAMP', 'TIMESTAMPTZ',
            
            // JSON types
            'JSON', 'JSONB',
            
            // Other common types
            'UUID', 'BYTEA', 'INET', 'CIDR', 'MACADDR'
        ]);
    };

    const loadTableData = async () => {
        try {
            setIsLoading(true);

            const filters = {
                searchQuery: '',
                filterColumn: '',
                filterValue: '',
                sortColumn,
                sortDirection
            };

            const response = await fetchTableData(selectedDatabase, selectedTable, 1, 1000, filters);

            if (response.status === 200 && response.data) {
                const apiData = response.data.data || [];
                const columns = response.data.columns || [];

                if (apiData.length > 0 || columns.length > 0) {
                    const columnsToUse = columns.length > 0 ? columns : generateColumnsFromData(apiData);
                    setTableColumns(columnsToUse);
                    setVisibleColumns(columnsToUse.map(col => col.name));
                    setOriginalTableData(apiData);
                    setFilteredTableData(apiData);
                    setTotalRows(apiData.length);
                    setPage(0);
                } else {
                    setTableColumns([]);
                    setVisibleColumns([]);
                    setOriginalTableData([]);
                    setFilteredTableData([]);
                    setTotalRows(0);
                    setPage(0);
                }
            } else {
                handleToastOpen('error', 'Invalid API response structure');
            }
        } catch (error) {
            handleToastOpen('error', 'Error loading table data: ' + (error.message || 'Unknown error'));
            setTableColumns([]);
            setVisibleColumns([]);
            setOriginalTableData([]);
            setFilteredTableData([]);
            setTotalRows(0);
            setPage(0);
        } finally {
            setIsLoading(false);
        }
    };

    const resetTableData = () => {
        setOriginalTableData([]);
        setFilteredTableData([]);
        setTableData([]);
        setTableColumns([]);
        setTotalRows(0);
        setPage(0);
        setSearchQuery('');
        setFilterColumn('');
        setFilterValue('');
        setSearchType('');
        setSortColumn('');
        setSortDirection('ASC');
        setSelectedRows([]);
        setVisibleColumns([]);
    };

    const handleDatabaseChange = (newValue) => {
        const databaseId = newValue ? (newValue.id || newValue.value || newValue) : '';
        setSelectedDatabase(databaseId);
        setSelectedTable('');
        resetTableData();

        if (databaseId && !openTabs.find(tab => tab.database === databaseId && !tab.table)) {
            const newTab = {
                id: `db-${databaseId}`,
                database: databaseId,
                label: databaseId,
                type: 'database'
            };
            setOpenTabs(prev => [...prev, newTab]);
            setActiveTab(openTabs.length);
        }
    };

    const handleTableChange = (newValue) => {
        const tableId = newValue ? (newValue.id || newValue.name || newValue.value || newValue) : '';
        setSelectedTable(tableId);
        setPage(0);
        setSelectedRows([]);
        clearAllFilters();

        if (tableId && selectedDatabase) {
            const tabId = `table-${selectedDatabase}-${tableId}`;
            if (!openTabs.find(tab => tab.id === tabId)) {
                const newTab = {
                    id: tabId,
                    database: selectedDatabase,
                    table: tableId,
                    label: `${selectedDatabase}.${tableId}`,
                    type: 'table'
                };
                setOpenTabs(prev => [...prev, newTab]);
                setActiveTab(openTabs.length);
            }
        }
    };

    const handleCloseTab = (tabIndex) => {
        setOpenTabs(prev => prev.filter((_, index) => index !== tabIndex));

        if (activeTab === tabIndex) {
            setActiveTab(Math.max(0, tabIndex - 1));
        } else if (activeTab > tabIndex) {
            setActiveTab(prev => prev - 1);
        }
    };

    const handleTabChange = (newActiveTab) => {
        setActiveTab(newActiveTab);

        if (newActiveTab < openTabs.length) {
            const tab = openTabs[newActiveTab];
            if (tab.database !== selectedDatabase) {
                setSelectedDatabase(tab.database);
            }
            if (tab.table && tab.table !== selectedTable) {
                setSelectedTable(tab.table);
            }
        }
    };

    const loadDatabases = async () => {
        try {
            setIsDatabaseLoading(true);
            const response = await fetchAllDatabases();

            if (response.status === 200 && response.data && Array.isArray(response.data)) {
                setDatabases(response.data);
            } else {
                handleToastOpen('error', 'Failed to load databases');
                setDatabases([]);
            }
        } catch (error) {
            handleToastOpen('error', 'Error loading databases: ' + (error.message || 'Unknown error'));
            setDatabases([]);
        } finally {
            setIsDatabaseLoading(false);
        }
    };

    useEffect(() => {
        loadDatabases();
    }, []);

    const loadTables = async (databaseId) => {
        try {
            setIsTableLoading(true);
            const response = await fetchDatabaseTables(databaseId);

            if (response.status === 200 && response.data) {
                let tableList = [];

                if (Array.isArray(response.data)) {
                    tableList = response.data;
                } else if (response.data.tables && Array.isArray(response.data.tables)) {
                    tableList = response.data.tables;
                } else {
                    tableList = [];
                }

                setTables(tableList);

                if (tableList.length === 0) {
                    setTables(['none']);
                }
            } else {
                handleToastOpen('error', 'Failed to load tables');
                setTables([]);
            }
        } catch (error) {
            handleToastOpen('error', 'Error loading tables: ' + (error.message || 'Unknown error'));
            setTables([]);
        } finally {
            setIsTableLoading(false);
        }
    };

    const handleSortChange = (column) => {
        if (sortColumn === column) {
            setSortDirection(prev => prev === 'ASC' ? 'DESC' : 'ASC');
        } else {
            setSortColumn(column);
            setSortDirection('ASC');
        }
        setPage(0);
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (newRowsPerPage) => {
        setRowsPerPage(newRowsPerPage);
        setPage(0);
    };

    const generateNextId = useCallback(() => {
        if (originalTableData.length === 0) return 1;

        const primaryKeyColumn = tableColumns.find(col => col.primaryKey);
        if (!primaryKeyColumn) return Date.now();

        const pkColumnName = primaryKeyColumn.name;

        const existingIds = originalTableData
            .map(row => row[pkColumnName])
            .filter(id => id != null && !isNaN(id))
            .map(id => Number(id))
            .filter(id => !isNaN(id));

        if (existingIds.length === 0) return 1;

        const maxId = Math.max(...existingIds);
        return maxId + 1;
    }, [originalTableData, tableColumns]);

    const handleAddNewRowInline = () => {
        const emptyRow = {};
        let primaryKeyColumn = null;

        tableColumns.forEach(col => {
            if (col.primaryKey) {
                primaryKeyColumn = col;
                emptyRow[col.name] = '';
            } else {
                emptyRow[col.name] = col.nullable ? '' : getDefaultValueForType(col.dataType);
            }
        });

        if (primaryKeyColumn) {
            const nextId = generateNextId();
            emptyRow[primaryKeyColumn.name] = nextId;
        }

        const newRow = {
            ...emptyRow,
            _isNew: true,
            _tempId: `new_${Date.now()}`
        };

        setOriginalTableData(prev => [newRow, ...prev]);
        setIsAddingNewRow(true);
        setEditingRow(newRow._tempId);
        setEditingData(newRow);
    };

    const getDefaultValueForType = (dataType) => {
        switch (dataType) {
            case 'INTEGER':
            case 'BIGINT':
            case 'SMALLINT':
                return 0;
            case 'NUMERIC':
            case 'DECIMAL':
            case 'REAL':
            case 'DOUBLE PRECISION':
                return 0.0;
            case 'BOOLEAN':
                return false;
            case 'DATE':
                return new Date().toISOString().split('T')[0];
            case 'TIMESTAMP':
            case 'TIMESTAMP WITH TIME ZONE':
                return new Date().toISOString();
            default:
                return '';
        }
    };

    const handleResetFilters = useCallback(() => {
        window.location.reload();
    }, []);

    const handleRefreshData = useCallback(async () => {
        if (selectedDatabase && selectedTable) {
            try {
                setIsLoading(true);
                await loadTableData();
                handleToastOpen('success', 'Data refreshed successfully');
            } catch (error) {
                handleToastOpen('error', 'Error refreshing data: ' + (error.message || 'Unknown error'));
            } finally {
                setIsLoading(false);
            }
        }
    }, [selectedDatabase, selectedTable, loadTableData, handleToastOpen]);

    const handleEditRow = (rowId) => {
        const row = originalTableData.find(r => (r.id || r._id || r._tempId) === rowId);
        setEditingRow(rowId);
        setEditingData({ ...row });
    };

    const handleSaveEdit = async () => {
        try {
            setIsLoading(true);

            if (editingData._isNew) {
                const cleanData = { ...editingData };
                delete cleanData._isNew;
                delete cleanData._tempId;

                const primaryKeyColumn = tableColumns.find(col => col.primaryKey);
                if (primaryKeyColumn && !cleanData[primaryKeyColumn.name]) {
                    cleanData[primaryKeyColumn.name] = generateNextId();
                }

                const response = await insertTableRow(selectedDatabase, selectedTable, cleanData);

                if (response.status === 201) {
                    setIsAddingNewRow(false);
                    await loadTableData();
                    handleToastOpen('success', 'Row added successfully');
                }
            } else {
                const rowId = editingData.id || editingData._id;
                const cleanData = { ...editingData };
                delete cleanData._isNew;
                delete cleanData._tempId;

                const response = await updateTableRow(selectedDatabase, selectedTable, rowId, cleanData);

                if (response.status === 200) {
                    await loadTableData();
                    handleToastOpen('success', 'Row updated successfully');
                }
            }

            setEditingRow(null);
            setEditingData({});
        } catch (error) {
            let errorMessage = 'Unknown error';

            if (error.response && error.response.data && error.response.data.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }

            handleToastOpen('error', errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancelEdit = () => {
        if (editingData._isNew) {
            setOriginalTableData(prev => prev.filter(row => row._tempId !== editingData._tempId));
            setIsAddingNewRow(false);
        }
        setEditingRow(null);
        setEditingData({});
    };

    const handleAddColumn = async () => {
        try {
            setIsLoading(true);
            const response = await addTableColumn(selectedDatabase, selectedTable, columnDialogData);

            if (response.status === 200) {
                await loadTableData();
                setShowColumnDialog(false);
                setColumnDialogData({});
                handleToastOpen('success', 'Column added successfully');
            }
        } catch (error) {
            handleToastOpen('error', 'Error adding column: ' + (error.message || 'Unknown error'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleModifyColumn = async () => {
        try {
            setIsLoading(true);

            const editData = {
                dataType: columnDialogData.dataType,
                newName: columnDialogData.newName
            };

            const response = await modifyTableColumn(
                selectedDatabase,
                selectedTable,
                columnDialogData.originalName,
                editData
            );

            if (response.status === 200) {
                await loadTableData();
                setShowColumnDialog(false);
                setColumnDialogData({});
                handleToastOpen('success', 'Column modified successfully');
            }
        } catch (error) {
            handleToastOpen('error', 'Error modifying column: ' + (error.message || 'Unknown error'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteColumn = async () => {
        try {
            setIsLoading(true);
            const response = await deleteTableColumn(selectedDatabase, selectedTable, columnDialogData.columnName);

            if (response.status === 200) {
                await loadTableData();
                setShowColumnDialog(false);
                setColumnDialogData({});
                handleToastOpen('success', 'Column deleted successfully');
            }
        } catch (error) {
            handleToastOpen('error', 'Error deleting column: ' + (error.message || 'Unknown error'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleExportData = async (format = 'csv') => {
        try {
            setIsLoading(true);

            if (filteredTableData.length === 0) {
                handleToastOpen('error', 'No data to export');
                return;
            }

            const filters = {
                searchQuery,
                filterColumn,
                filterValue
            };

            const config = {
                responseType: format === 'xlsx' || format === 'excel' ? 'arraybuffer' : 'text'
            };

            const response = await exportTableData(selectedDatabase, selectedTable, format, filters, config);

            if (response.status === 200) {
                if (format === 'csv') {
                    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${selectedTable}_export.csv`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                } else if (format === 'xlsx' || format === 'excel') {
                    const blob = new Blob([response.data], {
                        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                    });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${selectedTable}_export.xlsx`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                } else {
                    const jsonData = typeof response.data === 'string' ? response.data : JSON.stringify(response.data, null, 2);
                    const blob = new Blob([jsonData], { type: 'application/json' });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${selectedTable}_export.json`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                }

                handleToastOpen('success', 'Data exported successfully');
                setShowExportDialog(false);
            }
        } catch (error) {
            handleToastOpen('error', 'Error exporting data: ' + (error.response?.data?.message || error.message || 'Unknown error'));
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenExportDialog = () => {
        if (filteredTableData.length === 0) {
            handleToastOpen('error', 'No data to export');
            return;
        }
        setShowExportDialog(true);
    };

    const handleConfirmExport = () => {
        handleExportData(exportFormat);
    };

    const handleImportData = async () => {
        setShowImportDialog(false);
        if (!importFile) {
            handleToastOpen('error', 'Please select a file to import');
            return;
        }

        try {
            setIsLoading(true);
            const response = await importTableData(selectedDatabase, selectedTable, importFile, importOptions);

            if (response.status === 200) {
                await loadTableData();
                setImportFile(null);

                const { insertedRows, totalRows, errors } = response.data;
                let message = `Import completed. ${insertedRows}/${totalRows} rows imported successfully.`;

                if (errors && errors.length > 0) {
                    message += ` ${errors.length} errors occurred.`;
                }

                if (insertedRows > 0) {
                    handleToastOpen('success', message);
                } else {
                    handleToastOpen('error', message);
                }
            }
        } catch (error) {
            handleToastOpen('error', 'Error importing data: ' + (error.message || 'Unknown error'));
        } finally {
            setIsLoading(false);
        }
    };

    const getSearchTypesForColumn = (column) => {
        const baseTypes = [
            { value: 'contains', label: 'Contains' },
            { value: 'exact', label: 'Exact Match' },
            { value: 'starts_with', label: 'Starts With' },
            { value: 'ends_with', label: 'Ends With' },
            { value: 'not_contains', label: 'Does Not Contain' },
            { value: 'empty', label: 'Is Empty' },
            { value: 'not_empty', label: 'Is Not Empty' }
        ];

        if (column && column.dataType) {
            const dataType = column.dataType.toLowerCase();

            if (dataType.includes('int') || dataType.includes('numeric') || dataType.includes('decimal')) {
                return [
                    ...baseTypes,
                    { value: 'greater_than', label: 'Greater Than' },
                    { value: 'less_than', label: 'Less Than' },
                    { value: 'between', label: 'Between' }
                ];
            }

            if (dataType.includes('date') || dataType.includes('timestamp')) {
                return [
                    ...baseTypes,
                    { value: 'after', label: 'After Date' },
                    { value: 'before', label: 'Before Date' },
                    { value: 'between_dates', label: 'Between Dates' }
                ];
            }

            if (dataType.includes('boolean')) {
                return [
                    { value: 'exact', label: 'Exact Match' },
                    { value: 'empty', label: 'Is Empty' },
                    { value: 'not_empty', label: 'Is Not Empty' }
                ];
            }
        }

        return baseTypes;
    };

    const exportFilteredData = (format = 'csv') => {
        try {
            if (filteredTableData.length === 0) {
                handleToastOpen('error', 'No filtered data to export');
                return;
            }

            let content = '';
            let filename = `${selectedTable}_filtered`;

            if (format === 'csv') {
                const headers = visibleColumns.join(',');
                const rows = filteredTableData.map(row =>
                    visibleColumns.map(col => {
                        const value = row[col];
                        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
                            return `"${value.replace(/"/g, '""')}"`;
                        }
                        return value || '';
                    }).join(',')
                );

                content = headers + '\n' + rows.join('\n');
                filename += '.csv';

                const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                a.click();
                window.URL.revokeObjectURL(url);
            } else if (format === 'json') {
                const data = filteredTableData.map(row => {
                    const filteredRow = {};
                    visibleColumns.forEach(col => {
                        filteredRow[col] = row[col];
                    });
                    return filteredRow;
                });

                content = JSON.stringify(data, null, 2);
                filename += '.json';

                const blob = new Blob([content], { type: 'application/json' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                a.click();
                window.URL.revokeObjectURL(url);
            }

            handleToastOpen('success', `Filtered data exported successfully (${filteredTableData.length} rows)`);
        } catch (error) {
            handleToastOpen('error', 'Error exporting filtered data: ' + error.message);
        }
    };

    const getFilterStats = () => {
        const total = originalTableData.length;
        const filtered = filteredTableData.length;
        const selected = selectedRows.length;
        return {
            total,
            filtered,
            selected,
            isFiltered: total !== filtered,
            hasSearch: !!searchQuery,
            hasColumnFilter: !!(filterColumn && filterValue && searchType)
        };
    };

    const getCellStyle = (column, value, rowData) => {
        const baseStyle = {
            px: 2,
            py: 1,
            fontSize: '0.875rem',
            borderBottom: '1px solid #f1f5f9'
        };

        if (!column.nullable && (!value || value === '')) {
            baseStyle.backgroundColor = '#fef2f2';
            baseStyle.borderLeft = '2px solid #ef4444';
        }
        return baseStyle;
    };

    const addNewColumn = () => {
        const newColumn = {
            id: Date.now(),
            name: '',
            dataType: '',
            isPrimaryKey: false,
            allowNull: true,
            defaultValue: ''
        };
        setNewTableData(prev => ({
            ...prev,
            columns: [...prev.columns, newColumn]
        }));
    };

    const removeColumn = (id) => {
        if (newTableData.columns.length > 1) {
            setNewTableData(prev => ({
                ...prev,
                columns: prev.columns.filter(col => col.id !== id)
            }));
        }
    };

    const updateColumn = (id, field, value) => {
        setNewTableData(prev => ({
            ...prev,
            columns: prev.columns.map(col =>
                col.id === id ? { ...col, [field]: value } : col
            )
        }));
    };

    const handleCreateTable = async () => {
        try {
            setIsLoading(true);

            if (!selectedDatabase) {
                handleToastOpen('error', 'Please select a database first');
                return;
            }

            if (!newTableData.tableName.trim()) {
                handleToastOpen('error', 'Please enter a table name');
                return;
            }

            const validColumns = newTableData.columns.filter(col =>
                col.name.trim() && col.dataType.trim()
            );

            if (validColumns.length === 0) {
                handleToastOpen('error', 'Please add at least one column with name and data type');
                return;
            }

            const hasPrimaryKey = validColumns.some(col => col.isPrimaryKey);
            if (!hasPrimaryKey) {
                handleToastOpen('error', 'Please set at least one column as Primary Key');
                return;
            }

            const formattedColumns = validColumns.map(col => ({
                name: col.name,
                dataType: col.dataType,
                nullable: col.allowNull,
                defaultValue: col.defaultValue || null,
                primaryKey: col.isPrimaryKey,
                unique: col.isPrimaryKey
            }));

            const response = await createTable(
                selectedDatabase,
                newTableData.tableName,
                formattedColumns
            );

            if (response.status === 201) {
                handleToastOpen('success', 'Table created successfully');
                setShowNewTableDialog(false);
                setNewTableData({
                    tableName: '',
                    columns: [
                        {
                            id: 1,
                            name: '',
                            dataType: '',
                            isPrimaryKey: false,
                            allowNull: true,
                            defaultValue: ''
                        }
                    ]
                });
                setSelectedTable('');
                setTableData([]);
                setOriginalTableData([]);
                setFilteredTableData([]);
                setTableColumns([]);
                setVisibleColumns([]);
                setSelectedRows([]);
                setTotalRows(0);
                setPage(0);
                setEditingRow(null);
                setEditingData({});
                setIsAddingNewRow(false);
                setSearchQuery('');
                setFilterColumn('');
                setFilterValue('');
                setSearchType('');
                setSortColumn('');
                setSortDirection('ASC');
                setShowColumnDialog(false);
                setShowQueryDialog(false);
                setShowDeleteDialog(false);
                setShowImportDialog(false);
                setShowExportDialog(false);
                setActionMenu({ anchor: null, rowId: null });
                setHeaderMenu({ anchor: null, column: null });
                await loadTables(selectedDatabase);
            } else {
                handleToastOpen('error', 'Failed to create table');
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
            handleToastOpen('error', `Failed to create table: ${errorMessage}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseAllTabs = () => {
        setOpenTabs([]);
        setActiveTab(0);
    };

    const checkPermissions = () => {
        try {
            const permissionData = localStorage.getItem('permissionData');

            if (!permissionData) {
                return {
                    userDelete: false,
                    userDrop: false,
                    userTruncate: false,
                    userDeleteQuery: false
                };
            }
            let permissions = [];
            try {
                const parsed = JSON.parse(permissionData);
                if (Array.isArray(parsed)) {
                    permissions = parsed;
                } else if (typeof parsed === 'object') {
                    permissions = Object.values(parsed).flat();
                } else {
                    permissions = [parsed];
                }
            } catch (e) {
                permissions = permissionData.split(',').map(p => p.trim());
            }

            const userDelete = permissions.some(p =>
                p.includes('feature_user_delete') || p === 'feature_user_delete'
            );
            const userDrop = permissions.some(p =>
                p.includes('feature_user_drop') || p === 'feature_user_drop'
            );
            const userTruncate = permissions.some(p =>
                p.includes('feature_user_truncate') || p === 'feature_user_truncate'
            );
            return { userDelete, userDrop, userTruncate };
        } catch (error) {
            console.error('Error checking permissions:', error);
            return { userDelete: false, userDrop: false, userTruncate: false };
        }
    };

    useEffect(() => {
        if (selectedDatabase !== 'conas-prod-db') {
            setHasUserDelete(true);
            setHasUserDrop(true);
            setHasUserTruncate(true);
            return;
        }

        const permissions = checkPermissions();
        setHasUserDelete(permissions.userDelete);
        setHasUserDrop(permissions.userDrop);
        setHasUserTruncate(permissions.userTruncate);
    }, [selectedDatabase]);

    const handleExecuteQuery = async () => {
        try {
            setIsLoading(true);

            const response = await executeCustomQueryWithFilter(selectedDatabase, customQuery);

            if (response.status === 200 && response.data.success) {
                if (isFromSqlCreation) {
                    handleToastOpen('success', 'Table created successfully');
                    setShowQueryDialog(false);
                    setIsFromSqlCreation(false);

                    if (selectedDatabase) {
                        await loadTables(selectedDatabase);
                    }
                } else {
                    const queryResults = response.data.data || [];
                    const rowCount = response.data.rowCount || 0;
                    const processedQuery = response.data.processedQuery || customQuery;

                    const normalizedQuery = processedQuery.trim().toUpperCase();
                    let queryType = 'OTHER';
                    if (normalizedQuery.startsWith('INSERT')) queryType = 'INSERT';
                    else if (normalizedQuery.startsWith('UPDATE')) queryType = 'UPDATE';
                    else if (normalizedQuery.startsWith('DELETE')) queryType = 'DELETE';
                    else if (normalizedQuery.startsWith('SELECT')) queryType = 'SELECT';
                    else if (normalizedQuery.startsWith('CREATE')) queryType = 'CREATE';
                    else if (normalizedQuery.startsWith('ALTER')) queryType = 'ALTER';

                    if (selectedDatabase === 'conas-prod-db' && !hasUserDelete) {
                        handleToastOpen('error', 'Access denied: You do not have permission to execute DELETE queries on production database');
                        setShowQueryDialog(false);
                        return;
                    }

                    if (['INSERT', 'UPDATE', 'DELETE', 'ALTER'].includes(queryType)) {
                        await loadTableData();

                        if (queryType === 'INSERT') {
                            try {
                                const idMatch = processedQuery.match(/VALUES\s*\(\s*(\d+)/i);
                                if (idMatch && idMatch[1]) {
                                    const insertedId = idMatch[1];
                                    const primaryKeyColumn = tableColumns.find(col => col.primaryKey);

                                    if (primaryKeyColumn) {
                                        const filtered = originalTableData.filter(row =>
                                            String(row[primaryKeyColumn.name]) === String(insertedId)
                                        );

                                        if (filtered.length > 0) {
                                            setFilteredTableData(filtered);
                                            setTotalRows(filtered.length);
                                            setPage(0);
                                            setSearchQuery(`ID: ${insertedId} (newly inserted)`);
                                        }
                                    }
                                }
                            } catch (error) {
                                handleToastOpen('error', error);
                            }
                            handleToastOpen('success', `Insert successful. ${rowCount} row(s) inserted.`);
                        } else if (queryType === 'UPDATE') {
                            handleToastOpen('success', `Update successful. ${rowCount} row(s) updated.`);
                        } else if (queryType === 'DELETE') {
                            handleToastOpen('success', `Delete successful. ${rowCount} row(s) deleted.`);
                        } else if (queryType === 'ALTER') {
                            if (selectedDatabase && selectedTable) {
                                await loadTableData();
                            }
                            handleToastOpen('success', `Table altered successfully. Schema updated.`);
                        }
                        setActiveQuery(processedQuery);
                        setShowActiveQuery(true);
                        setShowQueryDialog(false);
                        setIsExpanded(false);

                    } else if (queryType === 'CREATE') {
                        if (selectedDatabase) {
                            await loadTables(selectedDatabase);

                            try {
                                const tableMatch = processedQuery.match(/CREATE\s+TABLE\s+(?:IF\s+NOT\s+EXISTS\s+)?([^\s(]+)/i);
                                if (tableMatch && tableMatch[1]) {
                                    const newTableName = tableMatch[1].replace(/[`"']/g, '').trim();
                                    setSelectedTable(newTableName);

                                    setTimeout(async () => {
                                        try {
                                            const tableResponse = await fetchTableData(selectedDatabase, newTableName, 1, 1000, {
                                                searchQuery: '',
                                                filterColumn: '',
                                                filterValue: '',
                                                sortColumn: '',
                                                sortDirection: 'ASC'
                                            });

                                            if (tableResponse.status === 200 && tableResponse.data) {
                                                const apiData = tableResponse.data.data || [];
                                                const columns = tableResponse.data.columns || [];

                                                const columnsToUse = columns.length > 0 ? columns : generateColumnsFromData(apiData);
                                                setTableColumns(columnsToUse);
                                                setVisibleColumns(columnsToUse.map(col => col.name));
                                                setOriginalTableData(apiData);
                                                setFilteredTableData(apiData);
                                                setTotalRows(apiData.length);
                                                setPage(0);
                                            }
                                        } catch (loadError) {
                                            handleToastOpen('error', loadError);
                                        }
                                    }, 500);

                                    handleToastOpen('success', `Table "${newTableName}" created and selected successfully.`);
                                } else {
                                    handleToastOpen('success', `Table created successfully.`);
                                }
                            } catch (error) {
                                handleToastOpen('error', error);
                            }
                        }
                        setActiveQuery(processedQuery);
                        setShowActiveQuery(true);
                        setShowQueryDialog(false);
                        setIsExpanded(false);
                    } else {
                        let queryColumns = [];
                        if (queryResults.length > 0) {
                            queryColumns = Object.keys(queryResults[0]).map((key, index) => ({
                                id: key,
                                name: key,
                                dataType: detectColumnType(queryResults[0][key]),
                                formattedType: detectColumnType(queryResults[0][key]),
                                nullable: true,
                                primaryKey: key === 'id' || key.toLowerCase().includes('id'),
                                position: index + 1
                            }));
                        } else {
                            queryColumns = tableColumns;
                        }

                        setOriginalTableData(queryResults);
                        setFilteredTableData(queryResults);
                        setTableColumns(queryColumns);
                        setVisibleColumns(queryColumns.map(col => col.name));
                        setTotalRows(rowCount);
                        setPage(0);
                        setSelectedRows([]);
                        setSearchQuery('');
                        setFilterColumn('');
                        setFilterValue('');
                        setSearchType('');
                        setActiveQuery(processedQuery);
                        setShowActiveQuery(true);
                        setShowQueryDialog(false);
                        setIsExpanded(false);
                        handleToastOpen('success', `Query executed successfully. ${rowCount} row(s) returned.`);
                    }
                }
            } else {
                setShowQueryDialog(false);
                const errorMessage = response.data?.message || response.data?.error || 'Query failed';
                handleToastOpen('error', `Query failed: ${errorMessage}`);

                if (isFromSqlCreation) {
                    setIsFromSqlCreation(false);
                }
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message ||
                error.response?.data?.error ||
                error.message ||
                'Unknown error';
            setShowQueryDialog(false);

            if (isFromSqlCreation) {
                handleToastOpen('error', `Table creation failed: ${errorMessage}`);
                setIsFromSqlCreation(false);
            } else {
                handleToastOpen('error', `Query failed: ${errorMessage}`);
            }
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancelActiveQuery = () => {
        setShowActiveQuery(false);
        setActiveQuery('');
        setIsExpanded(true);
        handleRefreshData();
    };

    const handleCancelQueryDialog = () => {
        setShowQueryDialog(false);
        setCustomQuery('');
        setSqlTemplateTab(0);
        setIsFromSqlCreation(false);
    };

    const getSqlTemplates = () => {
        const showDelete = selectedDatabase !== 'conas-prod-db' || hasUserDelete;
        const templates = {
            0: [
                {
                    name: 'Basic SELECT',
                    query: `SELECT * FROM ${selectedTable || 'standard_table'};`
                },
                {
                    name: 'SELECT with WHERE',
                    query: `SELECT ${tableColumns.slice(0, 3).map(col => col.name).join(', ') || 'id, name, email'}\nFROM ${selectedTable || 'your_table'}\nWHERE condition = 'value';`
                }
            ],
            1: [
                {
                    name: 'Basic INSERT',
                    query: `INSERT INTO ${selectedTable || 'your_table'} (${tableColumns.slice(0, 3).map(col => col.name).join(', ') || 'column1, column2, column3'})\nVALUES ('value1', 'value2', 'value3');`
                },
                {
                    name: 'INSERT with WHERE condition',
                    query: `INSERT INTO ${selectedTable || 'your_table'} (${tableColumns.slice(0, 2).map(col => col.name).join(', ') || 'column1, column2'})\nSELECT ${tableColumns.slice(0, 2).map(col => col.name).join(', ') || 'column1, column2'} FROM another_table\nWHERE condition = 'value';`
                }
            ],
            2: [
                {
                    name: 'Basic UPDATE',
                    query: `UPDATE ${selectedTable || 'your_table'}\nSET ${tableColumns[1]?.name || 'column_name'} = 'new_value'\nWHERE ${tableColumns[0]?.name || 'id'} = 1;`
                },
                {
                    name: 'UPDATE with WHERE condition',
                    query: `UPDATE ${selectedTable || 'your_table'}\nSET ${tableColumns[1]?.name || 'column_name'} = 'new_value'\nWHERE ${tableColumns[2]?.name || 'status'} = 'active' AND ${tableColumns[0]?.name || 'id'} > 10;`
                }
            ],
            3: showDelete ? [
                {
                    name: 'Basic DELETE',
                    query: `DELETE FROM ${selectedTable || 'your_table'}\nWHERE ${tableColumns[0]?.name || 'id'} = 1;`
                },
                {
                    name: 'DELETE with WHERE condition',
                    query: `DELETE FROM ${selectedTable || 'your_table'}\nWHERE ${tableColumns[2]?.name || 'status'} = 'inactive' AND ${tableColumns[0]?.name || 'id'} IN (1, 2, 3);`
                }
            ] : [],
            [showDelete ? 4 : 3]: [
                {
                    name: 'Basic CREATE TABLE',
                    query: `CREATE TABLE new_table (\n  id INT PRIMARY KEY,\n  name VARCHAR(255) NOT NULL,\n  created_at TIMESTAMP DEFAULT CURRENT_TIMESTAMP\n);`
                },
                {
                    name: 'CREATE TABLE with SELECT',
                    query: `CREATE TABLE new_table AS\nSELECT ${tableColumns.slice(0, 3).map(col => col.name).join(', ') || 'id, name, email'}\nFROM ${selectedTable || 'your_table'}\nWHERE condition = 'value';`
                }
            ],
            [showDelete ? 5 : 4]: [
                {
                    name: 'ALTER TABLE - Add Column',
                    query: `ALTER TABLE ${selectedTable || 'your_table'}\nADD COLUMN new_column VARCHAR(255) NULL;`
                },
                {
                    name: 'ALTER TABLE - Modify Column',
                    query: `ALTER TABLE standard_table\nALTER COLUMN column_name TYPE TEXT,\nALTER COLUMN column_name SET NOT NULL;`
                }
            ]
        };
        return templates[sqlTemplateTab] || [];
    };

    const handleExportQueryResults = () => {
        try {
            if (filteredTableData.length === 0) {
                handleToastOpen('error', 'No query results to export');
                return;
            }
            exportQueryResultsData('csv');
        } catch (error) {
            handleToastOpen('error', 'Error exporting query results: ' + error.message);
        }
    };

    const exportQueryResultsData = (format = 'csv') => {
        try {
            if (filteredTableData.length === 0) {
                handleToastOpen('error', 'No query results to export');
                return;
            }

            let content = '';
            let filename = `query_results_${new Date().toISOString().slice(0, 10)}`;

            if (format === 'csv') {
                const headers = visibleColumns.join(',');
                const rows = filteredTableData.map(row =>
                    visibleColumns.map(col => {
                        const value = row[col];
                        if (typeof value === 'string' && (value.includes(',') || value.includes('"'))) {
                            return `"${value.replace(/"/g, '""')}"`;
                        }
                        return value || '';
                    }).join(',')
                );

                content = headers + '\n' + rows.join('\n');
                filename += '.csv';

                const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);

            } else if (format === 'json') {
                const data = filteredTableData.map(row => {
                    const filteredRow = {};
                    visibleColumns.forEach(col => {
                        filteredRow[col] = row[col];
                    });
                    return filteredRow;
                });

                content = JSON.stringify(data, null, 2);
                filename += '.json';

                const blob = new Blob([content], { type: 'application/json' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            }

            handleToastOpen('success', `Query results exported successfully (${filteredTableData.length} rows)`);
        } catch (error) {
            handleToastOpen('error', 'Error exporting query results: ' + error.message);
        }
    };

    const filterStats = getFilterStats();
    const selectedColumn = tableColumns.find(col => col.name === filterColumn);
    const availableSearchTypes = getSearchTypesForColumn(selectedColumn);
    const lastPage = Math.ceil(totalRows / rowsPerPage) - 1;
    

    return {
        openTabs,
        databases,
        selectedDatabase,
        tables,
        selectedTable,
        tableData,
        filteredTableData,
        tableColumns,
        dataTypes,
        totalRows,
        isLoading,
        isDatabaseLoading,
        isTableLoading,
        page,
        rowsPerPage,
        searchQuery,
        filterColumn,
        filterValue,
        searchType,
        sortColumn,
        sortDirection,
        editingRow,
        editingData,
        selectedRows,
        visibleColumns,
        showColumnDrawer,
        showQueryDialog,
        customQuery,
        showColumnDialog,
        columnDialogMode,
        columnDialogData,
        showImportDialog,
        importFile,
        importOptions,
        activeTab,
        isAddingNewRow,
        filterStats,
        isExpanded,
        availableSearchTypes,
        headerMenu,
        actionMenu,
        selectedColumn,
        showDeleteDialog,
        showNewTableDialog,
        deleteType,
        sqlTemplateTab,
        hasUserDelete,
        hasUserDrop,
        hasUserTruncate,
        addNewColumn,
        removeColumn,
        updateColumn,
        handleExportQueryResults,
        newTableData,
        setNewTableData,
        setEditingData,
        setSelectedRows,
        setVisibleColumns,
        setShowColumnDrawer,
        setShowQueryDialog,
        setCustomQuery,
        setShowColumnDialog,
        setColumnDialogMode,
        setColumnDialogData,
        setShowImportDialog,
        setImportFile,
        setImportOptions,
        setIsExpanded,
        setShowDeleteDialog,
        setShowNewTableDialog,
        setDeleteType,
        setSqlTemplateTab,
        setHeaderMenu,
        setActionMenu,
        handleCloseAllTabs,
        handleDatabaseChange,
        handleTableChange,
        handleRefreshData,
        handleSearchChange,
        handleFilterChange,
        handleSearchTypeChange,
        handleSortChange,
        handlePageChange,
        handleRowsPerPageChange,
        clearAllFilters,
        exportFilteredData,
        handleAddNewRowInline,
        handleEditRow,
        handleSaveEdit,
        handleCancelEdit,
        handleAddColumn,
        handleModifyColumn,
        handleCreateTable,
        handleDeleteColumn,
        handleExecuteQuery,
        handleImportData,
        handleCloseTab,
        handleTabChange,
        getCellStyle,
        handleToastOpen,
        handleResetFilters,
        handleUnifiedDelete,
        showExportDialog,
        setShowExportDialog,
        exportFormat,
        setExportFormat,
        handleOpenExportDialog,
        handleConfirmExport,
        setHasUserDelete,
        activeQuery,
        showActiveQuery,
        handleCancelActiveQuery,
        handleCancelQueryDialog,
        getSqlTemplates,
        setCreateTableMenu,
        createTableMenu,
        setIsFromSqlCreation,
        isFromSqlCreation,
        lastPage
    };
};

export default DataManagementVM;